import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, Plus, Edit, Trash2, Eye, EyeOff,
  Stethoscope, UserPlus, Loader2, Star, MapPin,
  GraduationCap, Phone, Mail
} from "lucide-react";
import { toast } from "sonner";

const PROFESSIONAL_TYPES = [
  { value: "doctor", label: "Doctor (MD/DO)", icon: "🩺" },
  { value: "nurse", label: "Nurse (RN/NP)", icon: "👨‍⚕️" },
  { value: "pharmacist", label: "Pharmacist (PharmD)", icon: "💊" },
  { value: "dietitian", label: "Dietitian/Nutritionist (RD)", icon: "🥗" },
  { value: "diabetes_educator", label: "Diabetes Educator (CDE)", icon: "📚" },
];

const CUISINE_OPTIONS = [
  { value: "african", label: "African Cuisine" },
  { value: "nigerian", label: "Nigerian Cuisine" },
  { value: "western", label: "Western/Continental" },
  { value: "asian", label: "Asian Cuisine" },
  { value: "middle_eastern", label: "Middle Eastern" },
  { value: "vegetarian", label: "Vegetarian/Vegan" },
  { value: "mediterranean", label: "Mediterranean" },
  { value: "low_carb", label: "Low Carb/Keto" },
];

const PRICING_TYPES = [
  { value: "free_consultation", label: "Free Initial Consultation" },
  { value: "per_consultation", label: "Per Consultation Fee" },
  { value: "monthly_subscription", label: "Monthly Subscription" },
  { value: "insurance_only", label: "Insurance/HMO Only" },
];

const AdminMarketplace = () => {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const [formData, setFormData] = useState({
    display_name: "",
    title: "",
    organization_name: "",
    professional_type: "dietitian",
    credentials: "",
    bio: "",
    specializations: "",
    location_city: "",
    location_country: "Nigeria",
    phone: "",
    email: "",
    consultation_fee: "",
    pricing_type: "free_consultation",
    pricing_details: "",
    is_featured: false,
    is_active: true,
    years_experience: "",
    languages: "English",
    cuisine_specializations: [],
    qualifications: "",
    accepts_insurance: false,
    photo_url: "",
  });

  useEffect(() => {
    fetchProviders();
  }, [token]);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API}/admin/directory/providers`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      }
    } catch (error) {
      console.error("Failed to fetch providers:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      display_name: "",
      title: "",
      organization_name: "",
      professional_type: "dietitian",
      credentials: "",
      bio: "",
      specializations: "",
      location_city: "",
      location_country: "Nigeria",
      phone: "",
      email: "",
      consultation_fee: "",
      pricing_type: "free_consultation",
      pricing_details: "",
      is_featured: false,
      is_active: true,
      years_experience: "",
      languages: "English",
      cuisine_specializations: [],
      qualifications: "",
      accepts_insurance: false,
      photo_url: "",
    });
    setPhotoPreview(null);
    setEditingProvider(null);
  };

  const handleEdit = (provider) => {
    setFormData({
      display_name: provider.display_name || "",
      title: provider.title || "",
      organization_name: provider.organization_name || "",
      professional_type: provider.professional_type || "dietitian",
      credentials: provider.credentials || "",
      bio: provider.bio || "",
      specializations: (provider.specializations || []).join(", "),
      location_city: provider.location_city || provider.location || "",
      location_country: provider.location_country || "Nigeria",
      phone: provider.phone || provider.contact_phone || "",
      email: provider.email || provider.contact_email || "",
      consultation_fee: provider.consultation_fee?.toString() || "",
      pricing_type: provider.pricing_type || "free_consultation",
      pricing_details: provider.pricing_details || "",
      is_featured: provider.is_featured || false,
      is_active: provider.is_active !== false,
      years_experience: provider.years_experience?.toString() || "",
      languages: (provider.languages || ["English"]).join(", "),
      cuisine_specializations: provider.cuisine_specializations || [],
      qualifications: (provider.qualifications || []).join(", "),
      accepts_insurance: provider.accepts_insurance || false,
      photo_url: provider.photo_url || "",
    });
    setPhotoPreview(provider.photo_url || null);
    setEditingProvider(provider);
    setShowAddModal(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result;
      setPhotoPreview(base64);
      setFormData({ ...formData, photo_url: base64 });
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!formData.display_name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        specializations: formData.specializations.split(",").map(s => s.trim()).filter(Boolean),
        languages: formData.languages.split(",").map(s => s.trim()).filter(Boolean),
        qualifications: formData.qualifications.split(",").map(s => s.trim()).filter(Boolean),
        consultation_fee: formData.consultation_fee ? parseFloat(formData.consultation_fee) : null,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        contact_email: formData.email,
        contact_phone: formData.phone,
      };

      const url = editingProvider 
        ? `${API}/admin/directory/providers/${editingProvider.listing_id}`
        : `${API}/admin/directory/providers`;
      
      const response = await fetch(url, {
        method: editingProvider ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingProvider ? "Provider updated!" : "Provider added to marketplace!");
        setShowAddModal(false);
        resetForm();
        fetchProviders();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save provider");
      }
    } catch (error) {
      toast.error("Failed to save provider");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (provider) => {
    try {
      const response = await fetch(`${API}/admin/directory/providers/${provider.listing_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success(`${provider.display_name} removed from marketplace`);
        setDeleteTarget(null);
        fetchProviders();
      } else {
        toast.error("Failed to remove provider");
      }
    } catch (error) {
      toast.error("Failed to remove provider");
    }
  };

  const toggleVisibility = async (provider) => {
    try {
      const response = await fetch(`${API}/admin/directory/providers/${provider.listing_id}/toggle`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        toast.success(provider.is_active ? "Provider hidden from marketplace" : "Provider visible on marketplace");
        fetchProviders();
      }
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const filteredProviders = providers.filter(p => {
    const matchesSearch = 
      p.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || p.professional_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getProfessionalTypeLabel = (type) => {
    return PROFESSIONAL_TYPES.find(t => t.value === type)?.label || type;
  };

  const getProfessionalTypeIcon = (type) => {
    return PROFESSIONAL_TYPES.find(t => t.value === type)?.icon || "👤";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Healthcare Provider Marketplace</h1>
          <p className="text-muted-foreground">Manage providers visible to patients in the marketplace</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Provider
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
        {PROFESSIONAL_TYPES.map(type => {
          const count = providers.filter(p => p.professional_type === type.value).length;
          return (
            <Card key={type.value} className="bg-white">
              <CardContent className="p-4 text-center">
                <span className="text-2xl">{type.icon}</span>
                <p className="text-xl font-bold text-secondary mt-1">{count}</p>
                <p className="text-xs text-muted-foreground">{type.label.split(" ")[0]}s</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search providers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {PROFESSIONAL_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.icon} {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Providers List */}
      <Card className="bg-white">
        <CardContent className="p-0">
          {filteredProviders.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary mb-2">No Providers Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add healthcare providers to show them in the patient marketplace.
              </p>
              <Button onClick={() => { resetForm(); setShowAddModal(true); }} className="bg-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Provider
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {filteredProviders.map((provider) => (
                <div 
                  key={provider.listing_id} 
                  className={`p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${!provider.is_active ? 'opacity-50' : ''}`}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden flex-shrink-0">
                    {provider.photo_url ? (
                      <img src={provider.photo_url} alt={provider.display_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-primary/10 flex items-center justify-center text-2xl">
                        {getProfessionalTypeIcon(provider.professional_type)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-secondary">{provider.display_name}</h4>
                      {provider.is_featured && (
                        <Badge className="bg-yellow-100 text-yellow-700">
                          <Star className="w-3 h-3 mr-1" /> Featured
                        </Badge>
                      )}
                      {!provider.is_active && (
                        <Badge variant="secondary">Hidden</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {provider.title}
                      {provider.organization_name && ` • ${provider.organization_name}`}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Badge variant="outline" className="text-xs">
                          {getProfessionalTypeLabel(provider.professional_type)}
                        </Badge>
                      </span>
                      {(provider.location_city || provider.location) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {provider.location_city || provider.location}{provider.location_country && `, ${provider.location_country}`}
                        </span>
                      )}
                      {(provider.email || provider.contact_email) && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {provider.email || provider.contact_email}
                        </span>
                      )}
                      {provider.cuisine_specializations && provider.cuisine_specializations.length > 0 && (
                        <span className="flex items-center gap-1">
                          🍽️ {provider.cuisine_specializations.slice(0, 2).join(", ")}
                          {provider.cuisine_specializations.length > 2 && ` +${provider.cuisine_specializations.length - 2}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleVisibility(provider)}
                      title={provider.is_active ? "Hide from marketplace" : "Show on marketplace"}
                    >
                      {provider.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(provider)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(provider)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-provider-${provider.listing_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) resetForm(); setShowAddModal(open); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              {editingProvider ? "Edit Provider" : "Add Healthcare Provider"}
            </DialogTitle>
            <DialogDescription>
              {editingProvider ? "Update provider information" : "Add a new healthcare provider to the marketplace"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Photo Upload */}
            <div className="space-y-2 md:col-span-2">
              <Label>Provider Photo</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0 border-2 border-dashed border-gray-300">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Stethoscope className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">JPEG, PNG or WebP. Max 2MB.</p>
                </div>
                {photoPreview && (
                  <Button variant="ghost" size="sm" onClick={() => { setPhotoPreview(null); setFormData({ ...formData, photo_url: "" }); }} className="text-red-500">
                    Remove
                  </Button>
                )}
              </div>
            </div>

            {/* Basic Info */}
            <div className="space-y-2">
              <Label htmlFor="display_name">Full Name *</Label>
              <Input
                id="display_name"
                placeholder="Dr. John Smith"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="professional_type">Professional Type *</Label>
              <Select 
                value={formData.professional_type} 
                onValueChange={(v) => setFormData({ ...formData, professional_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROFESSIONAL_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Professional Title</Label>
              <Input
                id="title"
                placeholder="e.g., Certified Diabetes Educator"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization_name">Organization / Clinic Name</Label>
              <Input
                id="organization_name"
                placeholder="e.g., Lagos Teaching Hospital"
                value={formData.organization_name}
                onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Where the provider works - adds credibility</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="credentials">Credentials</Label>
              <Input
                id="credentials"
                placeholder="e.g., MD, RD, CDE, PharmD"
                value={formData.credentials}
                onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="doctor@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="+234 800 000 0000"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_city">City *</Label>
              <Input
                id="location_city"
                placeholder="Lagos"
                value={formData.location_city}
                onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_country">Country</Label>
              <Input
                id="location_country"
                placeholder="Nigeria"
                value={formData.location_country}
                onChange={(e) => setFormData({ ...formData, location_country: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="years_experience">Years of Experience</Label>
              <Input
                id="years_experience"
                type="number"
                placeholder="10"
                value={formData.years_experience}
                onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="consultation_fee">Consultation Fee (₦)</Label>
              <Input
                id="consultation_fee"
                type="number"
                placeholder="5000"
                value={formData.consultation_fee}
                onChange={(e) => setFormData({ ...formData, consultation_fee: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pricing_type">Pricing Type</Label>
              <Select 
                value={formData.pricing_type} 
                onValueChange={(v) => setFormData({ ...formData, pricing_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICING_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pricing_details">Pricing Details</Label>
              <Input
                id="pricing_details"
                placeholder="e.g., Free 30-minute initial consultation"
                value={formData.pricing_details}
                onChange={(e) => setFormData({ ...formData, pricing_details: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="languages">Languages (comma-separated)</Label>
              <Input
                id="languages"
                placeholder="English, Yoruba, Igbo"
                value={formData.languages}
                onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="specializations">Medical Specializations (comma-separated)</Label>
              <Input
                id="specializations"
                placeholder="Type 2 Diabetes, Weight Management, Pediatric Nutrition"
                value={formData.specializations}
                onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Cuisine Expertise</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                {CUISINE_OPTIONS.map(cuisine => (
                  <label key={cuisine.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.cuisine_specializations.includes(cuisine.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ 
                            ...formData, 
                            cuisine_specializations: [...formData.cuisine_specializations, cuisine.value] 
                          });
                        } else {
                          setFormData({ 
                            ...formData, 
                            cuisine_specializations: formData.cuisine_specializations.filter(c => c !== cuisine.value) 
                          });
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{cuisine.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="qualifications">Qualifications (comma-separated)</Label>
              <Input
                id="qualifications"
                placeholder="MSc Nutrition, Certified Diabetes Educator, Member - DAN"
                value={formData.qualifications}
                onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="bio">Bio / About</Label>
              <Textarea
                id="bio"
                placeholder="Brief description of the provider's background and expertise..."
                rows={3}
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              />
            </div>

            {/* Toggles */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Featured Provider</p>
                <p className="text-xs text-muted-foreground">Show at top of listings</p>
              </div>
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Accepts Insurance</p>
                <p className="text-xs text-muted-foreground">Provider accepts HMO/Insurance</p>
              </div>
              <Switch
                checked={formData.accepts_insurance}
                onCheckedChange={(v) => setFormData({ ...formData, accepts_insurance: v })}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => { resetForm(); setShowAddModal(false); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingProvider ? "Update Provider" : "Add Provider"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Provider</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently remove <strong>{deleteTarget?.display_name}</strong> from the marketplace? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminMarketplace;
